import type { SaleResource } from '@repo/api-client';

/**
 * How a sale was paid for — exactly one mode per sale, recorded as the terminal
 * submitted it and never inferred (CL-402, FR-409).
 *
 * Nothing below restates the union: it is read off `SaleResource`, so a fourth
 * mode added to the document fails to compile in this file rather than rendering
 * as `WALLET` on a manager's screen. `tickets/ticket-kinds.ts` does the same for
 * its own two-kind union and the reasoning is identical.
 *
 * This is the split a drawer is counted against, which is why it is a filter and
 * a column rather than a detail on the record: at cash-up the question is how
 * much of the day was cash, and a list that could not answer it would send
 * somebody to open every sale in turn.
 */
export type SalePaymentMode = SaleResource['paymentMode'];

/**
 * The modes, in the order they are offered.
 *
 * Cash first because it is the one that has to reconcile against a physical
 * drawer, and so the one somebody filtering by mode is usually after.
 */
export const SALE_PAYMENT_MODES: readonly SalePaymentMode[] = ['CASH', 'UPI', 'CARD'];

/**
 * `Record` rather than a function with a default, so exhaustiveness is the type
 * system's problem and not a runtime fallback nobody ever sees.
 *
 * "UPI" and "Card" as a person says them. Not the wire values — `CASH` in a
 * table column is the API's spelling leaking onto a screen, and the three are
 * read side by side down a column where one shouting is one too many.
 */
const MODE_LABELS: Record<SalePaymentMode, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
};

export const labelForPaymentMode = (mode: SalePaymentMode): string => MODE_LABELS[mode];
